import { useState } from "react";
import { Loader2, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, forgotPassword } from "@/lib/api";

const AuthCard = ({ initialMode = "login" }) => {
  const { login, signup, googleLogin } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", family_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login({ email: form.email, password: form.password });
      } else {
        await signup(form);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(form.email);
      setResetSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (mode === "forgot") {
    return (
      <div className="rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-[0_24px_48px_-12px_rgba(14,165,233,0.18)]" data-testid="auth-card">
        {resetSent ? (
          <div className="py-4 text-center" data-testid="forgot-success">
            <MailCheck className="mx-auto h-12 w-12 text-[#16A34A]" />
            <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Check your inbox</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              If that email exists in our family, a reset link is on its way. It works once and
              expires in one hour.
            </p>
            <button
              onClick={() => { setMode("login"); setResetSent(false); }}
              data-testid="forgot-back-btn"
              className="mt-6 rounded-full bg-slate-900 px-8 py-3 font-display text-sm font-semibold text-white"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            <h2 className="font-display text-xl font-bold text-slate-900">Forgot your password?</h2>
            <p className="mt-1 text-sm text-slate-500">
              Enter your email and we&apos;ll send you a reset link.
            </p>
            <form onSubmit={submitForgot} className="mt-5 grid gap-3" data-testid="forgot-form">
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                data-testid="forgot-email-input"
                placeholder="Email"
                className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
              />
              {error && (
                <p className="rounded-2xl bg-[#FFE5E5] px-4 py-3 text-sm font-semibold text-[#DC2626]" data-testid="forgot-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 py-3.5 font-display text-base font-semibold text-white disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Send reset link
              </button>
              <button
                type="button"
                onClick={() => { setMode("login"); setError(""); }}
                data-testid="forgot-cancel-btn"
                className="text-sm font-bold text-slate-500 hover:text-slate-800"
              >
                &larr; Back to sign in
              </button>
            </form>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-[#E2DDD0] bg-white p-8 shadow-[0_24px_48px_-12px_rgba(14,165,233,0.18)]" data-testid="auth-card">
      <button
        onClick={googleLogin}
        data-testid="signin-google-btn"
        className="flex w-full items-center justify-center gap-3 rounded-full border-2 border-[#E2DDD0] bg-white py-3 font-display text-base font-semibold text-slate-800 transition-all hover:border-slate-400"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
          <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.1 3.7-8.6z" />
          <path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-6-2.2-7-5.1l-3.9 3C3.2 21.3 7.3 24 12 24z" />
          <path fill="#FBBC05" d="M5 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3l-3.9-3C.4 8.3 0 10.1 0 12s.4 3.7 1.1 5.3l3.9-3z" />
          <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.2 2.7 1.1 6.7l3.9 3c1-2.9 3.8-5 7-5z" />
        </svg>
        Continue with Google
      </button>

      <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-slate-400">
        <span className="h-px flex-1 bg-[#E2DDD0]" /> or with email <span className="h-px flex-1 bg-[#E2DDD0]" />
      </div>

      <div className="mb-5 grid grid-cols-2 rounded-full bg-[#F8F5EC] p-1" data-testid="signin-mode-tabs">
        {["signup", "login"].map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setError(""); }}
            data-testid={`signin-tab-${m}`}
            className={`rounded-full py-2 text-sm font-bold transition-all ${
              mode === m ? "bg-slate-900 text-white shadow" : "text-slate-500"
            }`}
          >
            {m === "signup" ? "Create account" : "Sign in"}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="grid gap-3" data-testid="signin-form">
        {mode === "signup" && (
          <>
            <input
              required
              value={form.name}
              onChange={set("name")}
              data-testid="signin-name-input"
              placeholder="Your name"
              className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
            />
            <input
              value={form.family_name}
              onChange={set("family_name")}
              data-testid="signin-family-input"
              placeholder="Family name (e.g. Little Lights Family)"
              className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
            />
          </>
        )}
        <input
          required
          type="email"
          value={form.email}
          onChange={set("email")}
          data-testid="signin-email-input"
          placeholder="Email"
          className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
        />
        <input
          required
          type="password"
          minLength={8}
          value={form.password}
          onChange={set("password")}
          data-testid="signin-password-input"
          placeholder="Password (8+ characters)"
          className="rounded-2xl border border-[#E2DDD0] bg-[#F8F5EC] px-4 py-3 text-base outline-none focus:border-[#FFC107] focus:bg-white"
        />
        {mode === "login" && (
          <button
            type="button"
            onClick={() => { setMode("forgot"); setError(""); }}
            data-testid="forgot-password-link"
            className="justify-self-end text-xs font-bold text-[#0284C7] hover:underline"
          >
            Forgot password?
          </button>
        )}
        {error && (
          <p className="rounded-2xl bg-[#FFE5E5] px-4 py-3 text-sm font-semibold text-[#DC2626]" data-testid="signin-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="signin-submit-btn"
          className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 py-3.5 font-display text-base font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {mode === "login" ? "Sign in" : "Create my family account"}
        </button>
        <p className="text-center text-xs text-slate-400">
          Accounts are for parents and guardians. No child accounts, ever.
        </p>
      </form>
    </div>
  );
};

export default AuthCard;
